import {
  EmployNumRequestCode,
  EstablishRequestCode,
  ExcludedKeywordCategoryRequestCode,
  IncludedKeywordCategoryRequestCode,
} from '@doda/api-client/aspida/@types';
import { cassifyListAsLMS } from '../utils/convArrayUtil';
import { OP_SEARCH_ITEMS } from '../constants/searchItems';
import {
  EMPLOYMENT_NOT_JOIN,
  OPTIONS,
  SEARCH_CONDITION_ITEM_TITLE,
  WORKSTYLE,
} from '../constants/screenStructure/searchConditionItems';
import {
  AVERAGE_AGE_VALUE,
  BENEFITS_VALUE,
  EMPLOYMENT_VALUES,
  HOLIDAY_AND_WAY_OF_WORKING_VALUE,
  JOB_OFFER_KIND_VALUE,
  LANGUAGE_VALUE,
  LOWER_LIMIT_INCOME_VALUES,
  POSITIONS_VALUE,
  REQUIREMENT_VALUES,
  UPPER_LIMIT_INCOME_VALUES,
  WORKPLACE_ENVIRONMENT_VALUE,
} from '../constants/searchConditionLabelRequestValues';
import { SearchConditionLabelQuery } from '../services/getSearchConditionLabelAPI';
import { removeUndefinedFromObject } from '../utils/objectUtil';

/**
 * cookieをもとに検索条件ラベルAPIリクエストパラメータを生成
 * @param {string} cookieValue cookieのsearch_conditionの値（例：ha=50,0&op=17,18&dt=20240306172158）
 * @returns {SearchConditionLabelQuery} 検索条件ラベルAPIリクエストボディ
 */
export const createSearchConditionLabelRequestParams = (
  cookieValue: string
) => {
  // 取得したcookieからオブジェクトを生成
  const cookieSearchCondition: {
    [key: string]: string[];
  } = {};
  // TODO:PC新基盤側で生成されたsearch_condition1～3は二重エンコードされているので、PC SSRの画面ではエラーになってしまう。そのため、PC検索結果一覧画面がリリースされるまではここでさらにデコードするようにする。
  decodeURIComponent(cookieValue)
    .split('&')
    .forEach((condition) => {
      if (condition.startsWith('dt=')) return;
      const [key, value] = condition.split('=');
      cookieSearchCondition[key] = value.split(',');
    });

  // 職種を大・中・小分類に分ける
  const {
    lList: searchJobTypeLId,
    mList: searchJobTypeMId,
    sList: searchJobTypeSId,
  } = cassifyListAsLMS(cookieSearchCondition.oc);

  // 業種を大・小分類に分ける
  const { lList: industryLId, sList: industrySId } = cassifyListAsLMS(
    cookieSearchCondition.ind
  );

  // リクエストパラメータを生成
  const requestParams: SearchConditionLabelQuery = {};
  const opParams: SearchConditionLabelQuery = cookieSearchCondition.op
    ? createOpParams(cookieSearchCondition.op)
    : {};
  if (cookieSearchCondition.ar) requestParams.areaId = cookieSearchCondition.ar;
  if (cookieSearchCondition.pr)
    requestParams.prefectureId = cookieSearchCondition.pr;
  if (cookieSearchCondition.ci) requestParams.cityId = cookieSearchCondition.ci;
  if (cookieSearchCondition.wa) requestParams.wardId = cookieSearchCondition.wa;
  if (cookieSearchCondition.rt)
    requestParams.routeId = cookieSearchCondition.rt;
  if (cookieSearchCondition.st)
    requestParams.stationId = cookieSearchCondition.st;
  if (cookieSearchCondition.oc) {
    requestParams.searchJobTypeLId = searchJobTypeLId;
    requestParams.searchJobTypeMId = searchJobTypeMId;
    requestParams.searchJobTypeSId = searchJobTypeSId;
  }
  if (cookieSearchCondition.ind) {
    requestParams.industryLId = industryLId;
    requestParams.industrySId = industrySId;
  }
  if (cookieSearchCondition.kwc) {
    // キーワードカテゴリー「経験・スキル」はHydrogenでは「5」と扱っているため、コード値「9」の場合は変換
    if (cookieSearchCondition.kwc[0] === '9') {
      cookieSearchCondition.kwc[0] = '5';
    }
    requestParams.includedKeywordCategory = cookieSearchCondition
      .kwc[0] as IncludedKeywordCategoryRequestCode;
  }
  if (cookieSearchCondition.k) {
    const decodedKeword = cookieSearchCondition.k.map((str) => {
      return decodeURI(str);
    });
    requestParams.includedKeyword = decodedKeword.flatMap((word) =>
      word.split(/\s+/)
    );
  }
  if (cookieSearchCondition.niwc) {
    // キーワードカテゴリー「経験・スキル」はHydrogenでは「5」と扱っているため、コード値「9」の場合は変換
    if (cookieSearchCondition.niwc[0] === '9') {
      cookieSearchCondition.niwc[0] = '5';
    }
    requestParams.excludedKeywordCategory = cookieSearchCondition
      .niwc[0] as ExcludedKeywordCategoryRequestCode;
  }
  if (cookieSearchCondition.ni) {
    const decodedKeword = cookieSearchCondition.ni.map((str) => {
      return decodeURI(str);
    });
    requestParams.excludedKeyword = decodedKeword.flatMap((word) =>
      word.split(/\s+/)
    );
  }
  if (cookieSearchCondition.es)
    requestParams.establish =
      cookieSearchCondition.es as EstablishRequestCode[];
  if (cookieSearchCondition.ne)
    requestParams.employNum =
      cookieSearchCondition.ne as EmployNumRequestCode[];
  if (cookieSearchCondition.ha && cookieSearchCondition.ha[0] !== '0') {
    requestParams.lowerLimitIncome = createLowerLimitIncomeParam(
      cookieSearchCondition.ha
    );
  }
  if (cookieSearchCondition.ha && cookieSearchCondition.ha[1] !== '0') {
    requestParams.upperLimitIncome = createUpperLimitIncomeParam(
      cookieSearchCondition.ha
    );
  }
  if (cookieSearchCondition.tp) {
    requestParams.jobOfferKind = createJobOfferKindParam(
      cookieSearchCondition.tp
    );
  }

  const opParamsRemovedUndefined = removeUndefinedFromObject(opParams);
  return {
    ...requestParams,
    ...opParamsRemovedUndefined,
  };
};

/**
 * opから生成するパラメータはまとめてオブジェクトとして返す
 * @param optionParamList
 * @returns opから生成するパラメータ
 */
const createOpParams = (optionParamList: string[]) => {
  const employmentId: string[] = EMPLOYMENT_NOT_JOIN.list.map(
    (item) => item.id
  );
  const workstyleId: string[] = WORKSTYLE.map((item) => item.id);
  const requirementsId = OPTIONS.find(
    (obj) => obj.title === SEARCH_CONDITION_ITEM_TITLE.REQUIREMENT
  )?.list.map((item) => item.id);
  const workplaceEnvironmentId = OPTIONS.find(
    (obj) => obj.title === SEARCH_CONDITION_ITEM_TITLE.WORKPLACE_ENVIRONMENT
  )?.list.map((item) => item.id);
  const benefitId = OPTIONS.find(
    (obj) => obj.title === SEARCH_CONDITION_ITEM_TITLE.BENEFIT
  )?.list.map((item) => item.id);
  const languageId = OPTIONS.find(
    (obj) => obj.title === SEARCH_CONDITION_ITEM_TITLE.LANGUAGE
  )?.list.map((item) => item.id);
  const positionsId = OPTIONS.find(
    (obj) => obj.title === SEARCH_CONDITION_ITEM_TITLE.POSITION
  )?.list.map((item) => item.id);
  const averageAgeId = OPTIONS.find(
    (obj) => obj.title === SEARCH_CONDITION_ITEM_TITLE.AVERAGE_AGE
  )?.list.map((item) => item.id);
  return {
    isNewArrival:
      hasOptionParam(optionParamList, OP_SEARCH_ITEMS.newArrival.id) && true,
    isNearDeadline:
      hasOptionParam(optionParamList, OP_SEARCH_ITEMS.deadline.id) && true,
    employmentStatus: hasOptionParam(optionParamList, employmentId)
      ? createEmploymentParam(optionParamList)
      : undefined,
    holidayAndWayOfWorking: hasOptionParam(optionParamList, workstyleId)
      ? createWorkstyleParam(optionParamList)
      : undefined,
    requirements: hasOptionParam(optionParamList, requirementsId)
      ? createRequirementsParam(optionParamList)
      : undefined,
    workplaceEnvironment: hasOptionParam(
      optionParamList,
      workplaceEnvironmentId
    )
      ? createWorkplaceEnvironmentParam(optionParamList)
      : undefined,
    welfareBenefits: hasOptionParam(optionParamList, benefitId)
      ? createBenefitParam(optionParamList)
      : undefined,
    languageSkills: hasOptionParam(optionParamList, languageId)
      ? createLanguageParam(optionParamList)
      : undefined,
    positions: hasOptionParam(optionParamList, positionsId)
      ? createPositionsParam(optionParamList)
      : undefined,
    employeeAverageAge: hasOptionParam(optionParamList, averageAgeId)
      ? createAverageAgeParam(optionParamList)
      : undefined,
  };
};

const hasOptionParam = (
  optionParamList: string[],
  targetId: string | string[] | undefined
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TODO: targetId が undefined になる可能性があるので要修正
  return optionParamList.some((id) => targetId.includes(id));
};

/**
 * 雇用形態のパラメータ作成
 * @param op
 * @returns 雇用形態のパラメータ
 */
const createEmploymentParam = (op: string[]) => {
  const employmentParam = EMPLOYMENT_VALUES.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return employmentParam;
};

/**
 * 休日・働き方のパラメータ作成
 * @param op
 * @returns 休日・働き方のパラメータ
 */
const createWorkstyleParam = (op: string[]) => {
  const workstyleParam = HOLIDAY_AND_WAY_OF_WORKING_VALUE.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return workstyleParam;
};

/**
 * 募集・採用情報のパラメータ作成
 * @param op
 * @returns 募集・採用情報のパラメータ
 */
const createRequirementsParam = (op: string[]) => {
  const requirementsParam = REQUIREMENT_VALUES.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return requirementsParam;
};

/**
 * 会社・職場の環境のパラメータ作成
 * @param op
 * @returns 会社・職場の環境のパラメータ
 */
const createWorkplaceEnvironmentParam = (op: string[]) => {
  const workplaceEnvironmentParam = WORKPLACE_ENVIRONMENT_VALUE.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return workplaceEnvironmentParam;
};

/**
 * 待遇・福利厚生のパラメータ作成
 * @param op
 *@returns 待遇・福利厚生のパラメータ
 */
const createBenefitParam = (op: string[]) => {
  const benefitParam = BENEFITS_VALUE.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return benefitParam;
};

/**
 * 語学のパラメータ作成
 * @param op
 * @returns 語学のパラメータ
 */
const createLanguageParam = (op: string[]) => {
  const languageParam = LANGUAGE_VALUE.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return languageParam;
};

/**
 * 仕事内容のパラメータ作成
 * @param op
 * @returns 仕事内容のパラメータ
 */
const createPositionsParam = (op: string[]) => {
  const positionsParam = POSITIONS_VALUE.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return positionsParam;
};

/**
 * 社員の平均年齢のパラメータ作成
 * @param op
 * @returns 社員の平均年齢のパラメータ
 */
const createAverageAgeParam = (op: string[]) => {
  const averageAgeParam = AVERAGE_AGE_VALUE.filter((item) =>
    op.includes(item.value)
  ).map((item) => item.id);
  return averageAgeParam;
};

/**
 * 年収下限のパラメータ作成
 * @param ha
 * @returns 年収下限のパラメータ
 */
const createLowerLimitIncomeParam = (ha: string[]) => {
  const lowerLimitIncomeParam = LOWER_LIMIT_INCOME_VALUES.find(
    (income) => income.value === ha[0]
  )?.id;
  return lowerLimitIncomeParam;
};

/**
 * 年収上限のパラメータ作成
 * @param ha
 * @returns 年収上限のパラメータ
 */
const createUpperLimitIncomeParam = (ha: string[]) => {
  const lowerUpperIncomeParam = UPPER_LIMIT_INCOME_VALUES.find(
    (income) => income.value === ha[1]
  )?.id;
  return lowerUpperIncomeParam;
};

/**
 * 求人種別のパラメータ作成
 * @param tp
 * @returns 求人種別のパラメータ
 */
const createJobOfferKindParam = (tp: string[]) => {
  const jobOfferKindParam = JOB_OFFER_KIND_VALUE.filter((item) =>
    tp.includes(item.value)
  ).map((item) => item.id);
  return jobOfferKindParam[0];
};
